import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { usePermissions } from "@/hooks/usePermissions";
import AdminLayout from "@/components/admin/AdminLayout";
import { TableShell, Th, Td, EmptyRow } from "@/components/admin/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Users() {
  const { can } = usePermissions();
  const [items, setItems] = useState([]);
  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = () => api.get("/users").then((r) => setItems(r.data.filter((u) => u.role !== "customer"))).catch(() => {});
  useEffect(() => { load(); api.get("/roles").then((r) => setRoles(r.data)); api.get("/branches").then((r) => setBranches(r.data)); }, []);

  const openNew = () => { setEditing(null); setForm({ role: "sales_executive", is_active: true }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ ...u, password: "" }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) { const body = { ...form }; if (!body.password) delete body.password; await api.put(`/users/${editing.id}`, body); }
      else await api.post("/users", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const roleName = (k) => roles.find((r) => r.key === k)?.name || k;
  const branchName = (id) => branches.find((b) => b.id === id)?.name || "All Branches";

  return (
    <AdminLayout title="User Management" actions={can("users", "create") && <Button size="sm" className="rounded-full" onClick={openNew} data-testid="user-add"><Plus className="mr-1 h-4 w-4" />Add User</Button>}>
      <TableShell>
        <thead className="border-b border-border bg-secondary/40"><tr><Th>Name</Th><Th>Email</Th><Th>Username</Th><Th>Role</Th><Th>Branch</Th><Th>Status</Th><Th className="text-right">Actions</Th></tr></thead>
        <tbody className="divide-y divide-border">
          {items.length === 0 ? <EmptyRow colSpan={7} /> : items.map((u) => (
            <tr key={u.id} className="hover:bg-secondary/30" data-testid={`user-row-${u.id}`}>
              <Td className="font-medium">{u.name}</Td><Td>{u.email}</Td><Td>{u.username || "—"}</Td>
              <Td><span className="rounded-full bg-accent px-2 py-0.5 text-xs font-semibold text-accent-foreground">{roleName(u.role)}</span></Td>
              <Td>{branchName(u.branch_id)}</Td>
              <Td>{u.is_active === false ? <span className="text-xs text-destructive">Inactive</span> : <span className="text-xs text-emerald-600">Active</span>}</Td>
              <Td className="text-right">{can("users", "edit") && u.role !== "super_admin" && <button onClick={() => openEdit(u)} data-testid={`user-edit-${u.id}`} className="grid h-8 w-8 place-items-center rounded-md hover:bg-secondary"><Pencil className="h-4 w-4" /></button>}</Td>
            </tr>
          ))}
        </tbody>
      </TableShell>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} User</DialogTitle></DialogHeader>
          <form onSubmit={save} className="grid grid-cols-2 gap-3">
            <div><Label className="mb-1 block text-xs">Name</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
            <div><Label className="mb-1 block text-xs">Username</Label><Input value={form.username || ""} onChange={(e) => setForm({ ...form, username: e.target.value })} /></div>
            {!editing && <div className="col-span-2"><Label className="mb-1 block text-xs">Email</Label><Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>}
            <div><Label className="mb-1 block text-xs">Password {editing && "(blank = keep)"}</Label><Input type="password" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" /></div>
            <div><Label className="mb-1 block text-xs">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{roles.filter((r) => r.key !== "super_admin").map((r) => <SelectItem key={r.key} value={r.key}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label className="mb-1 block text-xs">Branch</Label>
              <Select value={form.branch_id || "none"} onValueChange={(v) => setForm({ ...form, branch_id: v === "none" ? null : v })}>
                <SelectTrigger data-testid="user-branch"><SelectValue placeholder="All branches" /></SelectTrigger>
                <SelectContent><SelectItem value="none">All Branches</SelectItem>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {editing && <label className="col-span-2 flex items-center gap-2 text-sm"><Switch checked={form.is_active !== false} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />Active</label>}
            <Button type="submit" className="col-span-2 rounded-full" data-testid="user-save">Save</Button>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
